import { Controller } from "@hotwired/stimulus";

// Keeps a message thread opened at its newest message, chat-style:
//
// - initial render → open the linked message, otherwise jump to the bottom
// - sending a reply re-renders the thread (stream replace or redirect),
//   which reconnects → jump to the bottom
// - an incoming broadcast APPENDS into the messages container; if the reader
//   is already at the bottom the thread eases down to show it, and if they
//   have scrolled up to read history it leaves them exactly where they are.
//
// "At the bottom" is tracked on scroll events rather than measured at append
// time — by the time the mutation fires, the new message has already grown
// scrollHeight and the reader no longer looks at-bottom.
//
// The scroll container differs per surface (pane column, modal body, the
// window itself on the full page), so the controller finds the nearest
// scrollable ancestor rather than assuming one.
const AT_BOTTOM_SLACK = 64;

// Reading position per thread, surviving reconnects. A live surface can
// re-render for reasons besides the reader's own actions (the inbox pane
// reloads when its entry is bumped by an incoming reply) — without this, the
// reconnect's initial jump yanks a reader who had scrolled up into history.
const readingState = new Map<string, { atBottom: boolean; scrollTop: number; anchor: string }>();

export default class extends Controller<HTMLElement> {
  static targets = ["scroller", "scrollButton"];

  declare readonly scrollerTarget: HTMLElement;
  declare readonly hasScrollerTarget: boolean;
  declare readonly scrollButtonTarget: HTMLElement;
  declare readonly hasScrollButtonTarget: boolean;

  private scroller!: Element;
  private atBottom = true;
  private anchor = "";
  private observer?: MutationObserver;
  private onScroll = () => {
    this.atBottom =
      this.scroller.scrollTop + this.scroller.clientHeight >= this.scroller.scrollHeight - AT_BOTTOM_SLACK;
    this.syncScrollButton();
  };

  connect(): void {
    this.scroller = this.scrollParent();
    this.anchor = window.location.hash;

    const remembered = this.element.id ? readingState.get(this.element.id) : undefined;
    if (remembered?.anchor !== this.anchor && this.scrollToLinkedMessage()) {
      this.onScroll();
    } else if (remembered && !remembered.atBottom) {
      this.scroller.scrollTop = remembered.scrollTop;
      this.atBottom = false;
    } else {
      this.toBottom("auto");
      this.atBottom = true;
    }

    this.scrollEventSource().addEventListener("scroll", this.onScroll, { passive: true });

    this.observer = new MutationObserver((mutations) => {
      const appended = mutations.some((m) => m.addedNodes.length > 0);
      if (appended && this.atBottom) this.toBottom("smooth");
      if (appended) this.syncScrollButton();
    });
    this.observer.observe(this.observedRoot(), { childList: true, subtree: true });
    this.syncScrollButton();
  }

  // Jump-to-bottom button (optional target): visible only while the reader
  // is away from the bottom, so it doubles as the "new content below" hint.
  jump(): void {
    this.toBottom("smooth");
  }

  disconnect(): void {
    if (this.element.id) {
      readingState.set(this.element.id, { atBottom: this.atBottom, scrollTop: this.scroller.scrollTop, anchor: this.anchor });
    }
    this.observer?.disconnect();
    this.scrollEventSource().removeEventListener("scroll", this.onScroll);
  }

  private scrollToLinkedMessage(): boolean {
    const match = window.location.hash.match(/^#(?:message-|conversation_message_)(\d+)$/);
    if (!match) return false;

    const message = document.getElementById(`conversation_message_${match[1]}`);
    if (!message || !this.element.contains(message)) return false;

    message.scrollIntoView({ block: "center", behavior: "auto" });
    return true;
  }

  private toBottom(behavior: ScrollBehavior): void {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (typeof this.scroller.scrollTo === "function") {
      this.scroller.scrollTo({ top: this.scroller.scrollHeight, behavior: reduced ? "auto" : behavior });
    } else {
      this.scroller.scrollTop = this.scroller.scrollHeight;
    }
  }

  // Scroll events fire on window for the document scroller, on the element
  // itself for an overflow container.
  private scrollEventSource(): EventTarget {
    return this.scroller === document.scrollingElement || this.scroller === document.documentElement
      ? window
      : this.scroller;
  }

  private syncScrollButton(): void {
    if (!this.hasScrollButtonTarget) return;

    this.scrollButtonTarget.classList.toggle("hidden", this.atBottom);
  }

  // Mutations happen inside the message list. With an explicit scroller
  // target that is the list; otherwise the controller element wraps it.
  private observedRoot(): Element {
    return this.hasScrollerTarget ? this.scrollerTarget : this.element;
  }

  private scrollParent(): Element {
    // An explicit target wins — the admin chat panels mount the controller
    // on a wrapper so the jump button can live outside the scroll area.
    if (this.hasScrollerTarget) return this.scrollerTarget;

    // Mounted directly on the overflow container (the case-conversation
    // surfaces), the element is its own scroller. Style alone decides —
    // scrollHeight is unreliable before first layout.
    const own = getComputedStyle(this.element).overflowY;
    if (own === "auto" || own === "scroll") return this.element;

    let el: HTMLElement | null = this.element.parentElement;
    while (el) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }
}
